import { KeyboardEvent, MouseEventHandler, PropsWithChildren, useMemo } from "react";
import { useDataId } from "utils/hooks";
import {
	ImageArrow,
	ImageMobileMenu,
	ImageX,
	ImageShare,
	ImageDownload,
	ImageFavorite,
	ImageInfo,
	ImageInfoCircle,
	ImagePlay,
	ImageMore,
	ImagePause,
	ImageVolumeFull,
	ImageVolumeHalf,
	ImageVolumeMute
} from "components/utility/images";
import { DefaultComponent } from "types/DefaultComponent";

/**
 * Union type for button icon types
 */
export type ButtonIconTypes =
	| "menu"
	| "close"
	| "download"
	| "favorite"
	| "share"
	| "info-popup"
	| "info-popup-circle"
	| "play"
	| "more"
	| "volume-full"
	| "volume-half"
	| "volume-mute"
	| "pause"
	| "toggle";

export interface ButtonIconProps extends DefaultComponent {
	ariaExpanded?: boolean;
	ariaControls?: string;
	className?: string;
	color?: string;
	fillOverride?: string;
	iconOverride?: string;
	isButton?: boolean;
	type: ButtonIconTypes;
	dataA?: string;
	dataAEvt?: string;
	dataAComp?: string;
	dataAName?: string;
	onClick?: (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	onKeyPress?: (e?: KeyboardEvent<HTMLButtonElement>) => void;
	onMouseEnter?: MouseEventHandler<HTMLButtonElement>;
	onMouseLeave?: MouseEventHandler<HTMLButtonElement>;
}

/**
 * ButtonIcon
 * @returns JSXElement
 */
function ButtonIcon({
	ariaLabel = "",
	ariaExpanded = false,
	ariaControls = "",
	className = "",
	color = "default",
	dataQA = "button-icon",
	dataQAPrefix = "",
	dataQASuffix = "",
	fillOverride,
	iconOverride,
	isButton = true,
	type,
	dataA = "",
	dataAEvt = "",
	dataAComp = "",
	dataAName = "",
	onClick,
	onKeyPress,
	onMouseEnter,
	onMouseLeave
}: PropsWithChildren<ButtonIconProps>) {
	const dataQAId = useDataId(`${dataQA}-${type}`, { prefix: dataQAPrefix, suffix: dataQASuffix });

	const genClassName = useMemo(() => {
		return `button-icon button-icon-${color} button-${type} ${className}`.trimEnd();
	}, [className, color, type]);

	/**
	 * Helper to return appropriate icon
	 * @returns JSXElement
	 */
	const getIcon = (): JSX.Element => {
		let icon: JSX.Element = <>&gt;</>;

		if (!iconOverride) {
			switch (type) {
				case "menu":
					icon = <ImageMobileMenu />;
					break;
				case "close":
					icon = <ImageX />;
					break;
				case "download":
					icon = <ImageDownload />;
					break;
				case "favorite":
					icon = <ImageFavorite />;
					break;
				case "share":
					icon = <ImageShare />;
					break;
				case "info-popup":
					icon = <ImageInfo />;
					break;
				case "info-popup-circle":
					icon = <ImageInfoCircle />;
					break;
				case "play":
					icon = <ImagePlay fillOverride={fillOverride} />;
					break;
				case "more":
					icon = <ImageMore />;
					break;
				case "volume-full":
					icon = <ImageVolumeFull fillOverride={fillOverride} />;
					break;
				case "volume-half":
					icon = <ImageVolumeHalf fillOverride={fillOverride} />;
					break;
				case "volume-mute":
					icon = <ImageVolumeMute fillOverride={fillOverride} />;
					break;
				case "pause":
					icon = <ImagePause fillOverride={fillOverride} />;
					break;
				case "toggle":
					icon = <ImageArrow />;
					break;
				default:
					break;
			}
		} else {
			if (iconOverride.split(".").pop() === "svg") {
				icon = (
					<object type="image/svg+xml" data={iconOverride} aria-label={`${type} icon`}></object>
				);
			}
			icon = <img src={iconOverride} alt={type} />;
		}

		return icon;
	};

	/**
	 * handleKeyPress
	 * @param e KeyboardEvent : Synthetic handler for keyboard event
	 * @see https://www.w3.org/TR/uievents-key/#keys-whitespace
	 */
	const handleKeyPress = (e: KeyboardEvent) => {
		switch (e.code) {
			case "Enter":
			case "Space":
				onKeyPress
					? onKeyPress()
					: onClick
						? onClick()
						: console.error("There is no method for handling this action.");
				break;
		}
	};

	return isButton ? (
		<button
			aria-controls={ariaControls}
			aria-label={`${ariaLabel || type}`}
			aria-expanded={ariaExpanded}
			className={genClassName}
			data-qa={dataQAId}
			data-a={dataA}
			data-a-evt={dataAEvt}
			data-a-comp={dataAComp}
			data-a-name={dataAName}
			type="button"
			onClick={onClick ? (e) => onClick(e) : undefined}
			onKeyUp={(e) => handleKeyPress(e)}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
		>
			{getIcon()}
		</button>
	) : (
		<div
			aria-label={`${ariaLabel || type}`}
			className={genClassName}
			data-qa={dataQAId}
			data-a={dataA}
			data-a-evt={dataAEvt}
			data-a-comp={dataAComp}
			data-a-name={dataAName}
		>
			{getIcon()}
		</div>
	);
}

export default ButtonIcon;
